import { HttpBackend, HttpClient, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { IFileUploadObject } from 'src/app/shared/components/file-uploader/file-upload-object.interface';
import { GetEDIAttachment, SaveEDIAttachments, SetLoading, UpdateUploadedAttachment } from 'src/app/shared/states/orders/orders.actions';
import { OrdersState } from 'src/app/shared/states/orders/orders.state';
import { IGetFileMetadataResponse } from '../interfaces/get-file-metadata-response.interface';
import { IFileWithMetadata } from '../interfaces/file-with-metadata.interface';
import { PDF_MIME_TYPE } from '../constants/constants';
import { blobToFile } from '../helpers/file-helpers';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class FileService {
  @Select(OrdersState.recentRequestedAttachmentMetadata) recentRequestedAttachmentMetadata$: Observable<IGetFileMetadataResponse | undefined>;
  @Select(OrdersState.orderFiles) orderFiles$: Observable<IFileUploadObject[]>;

  private httpClient: HttpClient;

  constructor(
    httpBackend: HttpBackend,
    private store: Store,
  ) {
    this.httpClient = new HttpClient(httpBackend);
  }

  saveAttachmentsList(payload, isEditMode: boolean, alreadyAttached: IFileUploadObject[], attachments?: IFileUploadObject[]): void {
    if (!isEditMode) {
      attachments?.map(x => x.orderId = payload?.orderId);
    }
    if (attachments) {
      if (attachments.some(x => x.isUploading)) {
        return;
      }
      this.store.dispatch(new SaveEDIAttachments(
        isEditMode ?
          attachments :
          [...alreadyAttached ?? [], ...attachments], payload
      ))
        .pipe(untilDestroyed(this), withLatestFrom(this.orderFiles$))
        .subscribe(([_, orderFiles]: [any, IFileUploadObject[]]) => {
          this.uploadFiles(orderFiles);
        });
    }
  }


  downloadFile(item: IFileUploadObject): void {
    this.store.dispatch(new GetEDIAttachment({id: item.id, name: item.name}))
      .pipe(untilDestroyed(this), withLatestFrom(this.recentRequestedAttachmentMetadata$))
      .subscribe(([_, metadata]) => {
        window.open(metadata.url, '_blank');
      });

  }

  async getFileWithMetadata(id: string, oldName: string, newName: string, updatedFile: File): Promise<IFileWithMetadata> {
   await this.store.dispatch(new GetEDIAttachment({id, name: oldName})).toPromise();
   const metadata = this.store.selectSnapshot(OrdersState.recentRequestedAttachmentMetadata);
   if (metadata) {
    const operationResult = { url: metadata.url, isSigned: metadata.isSigned, file: updatedFile};
    if (!updatedFile) {
      this.store.dispatch(new SetLoading(true));
      const s3FileResponse = await fetch(metadata.url);
      this.store.dispatch(new SetLoading(false));
      operationResult.file = blobToFile(await s3FileResponse?.blob(), newName, PDF_MIME_TYPE);
    }

      return operationResult;
   }
   return null;
  }
  
  private uploadFileAWSS3(fileuploadurl, file: File): Observable<any> {
    const headers = new HttpHeaders({'Content-Type': 'multipart/form-data'});
    const req = new HttpRequest(
      'PUT',
      fileuploadurl,
      file,
      {
        headers,
        //to receive uploaded percent
        //reportProgress: true
      },
    );
    
    return this.httpClient.request(req);
  }

  private async uploadFiles(files: IFileUploadObject[]): Promise<void> {
    this.store.dispatch(new SetLoading(true));
    await Promise.all(
      files.map(async file => {
      await this.uploadFile(file);
      })
    );
    this.store.dispatch(new SetLoading(false));
  }

  private async uploadFile(file: IFileUploadObject): Promise<void> {
    const stateFile = this.store.selectSnapshot(OrdersState.orderFiles)?.find(x => x.id === file.id);
      if (!stateFile.isUploading && stateFile.uploadCompleted !== false && file.downloadUrl && file.selectedFile) {

        try {
          const res = await this.uploadFileAWSS3(file.downloadUrl, file.selectedFile).toPromise();
          
          if (res.type === HttpEventType.Response) {
            this.store.dispatch(new UpdateUploadedAttachment(file.id));
          }
    
          if (res['error']) {
            console.error(res['error']);
          }
        } catch (error) {
          console.error('Error uploading file:', error);
        }
      }
   
  }
}
